import { type Sorting } from "@devexpress/dx-react-grid";
import { type User } from "@internal/user";
import { type SalesPageMetric } from "@north-beam/nb-client-side";
import {
  type SalesReportRowExtraDetail,
  type metrics,
  map,
  type RowWithChildren,
} from "@north-beam/nb-common";

import { TabView } from "../tab-table-section";

export interface ColumnDef {
  title: string;
  name: string;
  id: string;
  metric: SalesPageMetric;
  getCellValue?: (row: any, columnName: string) => any;
  tooltip?: string;
  comparison?: any;
  width: number;
  visible: boolean;
  options?: Record<string, any>;
  isNonModal?: boolean;
}

const getDefaultCellWidth = (title: string) => {
  if (title === "Spend") {
    return 180;
  }
  return Math.round(Math.max(title.length * 9.6, 120));
};

// Removes the attribution window like (1d) or (3d, 1st time) or (14d, Returning) from the name
export const normalizeMetricKey = (metricName: string) => {
  const name = metricName.replaceAll("Txn", "Transaction");
  if (name.startsWith("Forecasted")) {
    return name;
  }
  if (
    name.endsWith(", 1st time)") ||
    name.endsWith(", Returning)") ||
    name.endsWith(", Recurring)") ||
    name.endsWith(", Other)")
  ) {
    return name.replace(/\(\d{1,2}d, /, "(");
  }
  return name.replace(/ \(\d{1,2}d\)$/, "");
};

export const createMetricColumns = (metricsArray: SalesPageMetric[]) =>
  metricsArray
    .filter((v) => !v.hideFromTableDisplay)
    .map((metric) => {
      const metricName = metric.name.replaceAll("Txn", "Transaction");
      const columnDef: ColumnDef & { metric?: any } = {
        title: metricName,
        // IMPORTANT: This is used to key the like columns across different filters
        name: normalizeMetricKey(metricName),
        id: metric.id,
        getCellValue: (row: any) => row?.export?.[metric.id] ?? row?.value,
        tooltip: metric.descriptionHTML,
        visible: false,
        width: getDefaultCellWidth(metricName),
        metric,
        isNonModal: metric.isNonModal,
      };

      if (!metric.hideComparisonFromTableCell) {
        columnDef.comparison = {
          isPositiveChangeGood: metric.isPositiveChangeGood,
        };
      }

      return columnDef;
    });

export const generateCellHoverHTML = (
  node: RowWithChildren<TableableResponseRow>,
  metricsArray: SalesPageMetric[],
  user: User,
): Record<string, string> => {
  const columnIdToCellHoverHTML: Record<string, string> = {};

  for (const metric of metricsArray) {
    const metadata = metric.metadata ?? {};
    const isMetricDerivedFromConversionAttribution =
      metadata.metricId &&
      ["rev", "txns", "roas", "cac", "ecr"].includes(metadata.metricId);

    if (isMetricDerivedFromConversionAttribution || metadata.isGoalRelated) {
      if (node?.cellAnnotations?.visitAttributionInTraining) {
        columnIdToCellHoverHTML[metric.id] = `
          <p><b>In Training</b></p>
          <p class="mb-0">
            We are still collecting First-Party Data and developing this model.
            Values are subject to fluctuation and should be discounted.
          </p>`;
      } else if (node?.cellAnnotations?.awarenessCampaignAttributionTurnedOff) {
        columnIdToCellHoverHTML[metric.id] = `
          <p><b>Update</b></p>
          <p>
            We are making a temporary update to our view attribution model that
            will disable view-through credit to Awareness campaigns.
          </p>
          <p class="mb-0">
            Please reach out to your customer education team member if you have
            any questions.
          </p>`;
      } else if (
        (node?.name?.toLowerCase()?.includes("mntn") ||
          node?.platformId?.toLocaleLowerCase()?.includes("mntn")) &&
        user.featureFlags.enableMNTNHoverTooltip
      ) {
        columnIdToCellHoverHTML[metric.id] = `
          <p><b>Update</b></p>
          <p>
          Please use our MMM offering for attribution information.
          </p>
          <p class="mb-0">
          Reach out to your customer education team member if you have any questions.
          </p>`;
      }
    }
  }
  return columnIdToCellHoverHTML;
};

interface DailyData {
  current: metrics.MetricPoint;
  name: string;
  date: string | null;
  extraDetails: SalesReportRowExtraDetail[];
  description: { key: string; value: string }[];
}

export interface TableableResponseRow {
  id: string;
  platformId: string | null;
  parentId: string | null;
  description: { key: string; value: string }[];
  link?: string;
  name: string;
  extraDetails: SalesReportRowExtraDetail[];
  cellAnnotations: Record<string, boolean>;
  current: metrics.MetricPoint;
  compare: metrics.MetricPoint | null;
}

export type TableableResponseRowWithChildren =
  RowWithChildren<TableableResponseRow>;

export const createDailyDataRoot = (
  dailyData: Record<string, DailyData>,
  extraExportDimensionColumns: Set<string>,
  metricsArray: SalesPageMetric[],
) =>
  Object.values(dailyData).map((row: DailyData) => {
    const exportData: Record<string, any> = {};

    exportData.Name = row.name;
    exportData.Date = row.date;

    for (const { name, value } of row.extraDetails) {
      exportData[name] = value;
      extraExportDimensionColumns.add(name);
    }

    for (const { key, value } of row.description) {
      exportData[key] = value;
    }

    for (const metric of metricsArray) {
      exportData[metric.id] = metric.calculate(row.current);
    }

    return {
      export: exportData,
      link: {},
      display: {},
      changeFractions: {},
      rowMetadata: {},
      cellHoverHTML: {},
    };
  });

export const createNativeTableRoot = (
  data: TableableResponseRowWithChildren,
  extraExportDimensionColumns: Set<string>,
  metricsArray: SalesPageMetric[],
  search: string,
  granularity: TabView,
  user: User,
) => {
  if (!data) {
    return { children: [] };
  }

  const tableRoot = map(data, (node, level) => {
    const link: Record<string, any> = {};
    const exportData: Record<string, any> = {};
    const display: Record<string, string> = {};
    const changeFractions: Record<string, number | null> = {};
    const rowMetadata: Record<string, any> = {};
    const cellHoverHTML: Record<string, string> = generateCellHoverHTML(
      node,
      metricsArray,
      user,
    );

    rowMetadata.level = level;
    rowMetadata.point = node.current;
    rowMetadata.platformId = node.platformId;
    rowMetadata.extraDetails = node.extraDetails;
    rowMetadata.isPlatformView = granularity === TabView.PLATFORM;

    display.Name = node.name;
    exportData.Name = node.name;
    if (node.link) {
      link.Name = node.link + search;
    }

    for (const { name, value } of node.extraDetails) {
      exportData[name] = value;
      extraExportDimensionColumns.add(name);
    }

    for (const { key, value } of node.description) {
      display[key] = value;
      exportData[key] = value;
    }

    for (const metric of metricsArray) {
      const lowerCaseNamed = metric.name.toLowerCase();

      const isTikTokShopMetrics = [
        "transactionsTikTokShops",
        "revenueTikTokShops",
      ].includes(metric.id);

      // If this is a platform metric, show "N/A" in the Grand Total Row
      if (
        (lowerCaseNamed.includes("fb ") ||
          lowerCaseNamed.includes("google ") ||
          (lowerCaseNamed.includes("tiktok ") && !isTikTokShopMetrics)) &&
        display.Name === "Grand Total"
      ) {
        exportData[metric.id] = "N/A";
        display[metric.id] = "N/A";
        changeFractions[metric.id] = 0;
      } else {
        const current = metric.calculate(node.current);
        let changeFraction: number | null = null;
        if (node.compare) {
          const compare = metric.calculate(node.compare);
          changeFraction = (current - compare) / compare;
        }

        exportData[metric.id] = current;
        display[metric.id] = metric.formatter(
          current,
          user.currencies.currentCurrency,
          {
            precision: "exact",
          },
        );
        changeFractions[metric.id] = changeFraction;
      }
    }

    return {
      key: node.id ?? node.link,
      parentId: node.parentId,
      link,
      export: exportData,
      display,
      changeFractions,
      rowMetadata,
      cellHoverHTML,
    };
  });

  // Disable tree view when on platform
  if (granularity === TabView.PLATFORM) {
    tableRoot.children.forEach((row: any) => {
      row.children = [];
    });
  }

  return tableRoot;
};

const sortRows = (
  rows: any[],
  columnId: string,
  direction: Sorting["direction"],
) => {
  return rows.sort((a: any, b: any) => {
    const aValue = a.export[columnId];
    const bValue = b.export[columnId];

    if (aValue === bValue) {
      return 0;
    }

    if (aValue === undefined || isNaN(aValue)) {
      return 1;
    }
    if (bValue === undefined || isNaN(bValue)) {
      return -1;
    }
    if (aValue === Infinity) {
      return 1;
    }
    if (bValue === Infinity) {
      return -1;
    }

    if (aValue < bValue) {
      return direction === "asc" ? -1 : 1;
    }
    if (aValue > bValue) {
      return direction === "asc" ? 1 : -1;
    }

    return 0;
  });
};

export const recursiveSort = (
  rows: any[],
  columnId: string,
  direction: Sorting["direction"],
) =>
  sortRows(rows, columnId, direction).map((row) => {
    if (row.children) {
      row.children = recursiveSort(row.children, columnId, direction);
    }

    return row;
  });

export const getChildRows = (row: any | null, rootRows: any[] | null) => {
  const hasChildren = row?.children?.length > 0;
  if (hasChildren) {
    return row.children;
  }
  // If has row but no children, then row is leaf
  if (row) {
    return null;
  }
  return rootRows;
};

export const filter0SpendAnd0TransactionRows = (rows: any[]) => {
  return rows.filter((row) => {
    const hasChildren = row.children?.length > 0;
    if (hasChildren) {
      row.children = filter0SpendAnd0TransactionRows(row.children);
      return row.children.length > 0;
    }

    const txnKeys = Object.keys(row.export).filter((key) => {
      const lowerCaseKey = key.toLowerCase();
      return lowerCaseKey.startsWith("txns");
    });
    if (txnKeys.length && row.export.spend !== undefined) {
      const numOfTxns = txnKeys.reduce((acc, key) => acc + row.export[key], 0);
      const is0SpendAnd0Txns = row.export.spend === 0 && numOfTxns === 0;
      return !is0SpendAnd0Txns;
    }

    return true;
  });
};
